import { Component, NgZone, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, PopoverController, ToastController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ModalComponent } from 'src/app/components/modals/modal/modal.component';
import { PopoverComponent } from 'src/app/components/popovers/popover/popover.component';
import { ErrorService } from 'src/app/services/error/error.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PagingService } from 'src/app/services/paging/paging.service';
import { IPrinter } from 'src/app/services/printer/models/printer.model';
import { IQueue } from 'src/app/services/printer/models/queue.model';
import { PrinterService } from 'src/app/services/printer/printer.service';
import { SanitizationService } from 'src/app/services/sanitization/sanitization.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { UserService } from 'src/app/services/user/user.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { DocumentPrintService } from 'src/app/services/document-print/document-print.service';
import { UploadState } from 'src/app/services/document-print/models/document-upload-session.model';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { ShareExtensionService, ShareSupersededError } from 'src/app/services/share-extension/share-extension.service';
import { IShareItem } from '@app/services/share-extension/models/share-item.model';

@Component({
  selector: 'app-document-print',
  templateUrl: './document-print.page.html',
  styleUrls: ['./document-print.page.scss'],
  standalone: false
})
export class DocumentPrintPage implements OnInit, OnDestroy {

  // ############### SIGNALS ###############

  // A shared file arrives here as a plain File with no App Group counterpart: ShareExtensionService deletes the on-disk copy as soon as it has read it, so this page owns nothing to clean up.
  public selectedFile         = signal<File | null>(null);
  public selectedQueue        = signal<IQueue | null>(null);
  public queueList            = signal<IQueue[]>([]);
  public pagingObject         = signal<any>({});
  public storedNetworkFilters = signal<any[]>([]);
  public isDragOver           = signal<boolean>(false);
  public validationError      = signal<string | null>(null);
  private setFileRequestId = 0;
  public isOnline             = this.networkService.hasInternetAccess;

  // ############### PAGINATION / FILTER STATE ###############

  public currentPage  = 0;
  private pageSize    = 9;
  private searchTerm  = '';
  private storedNetworkIds: string[] = [];
  private tenantNetworks: any;
  private printerListData: IPrinter[] = [];
  private filterObjectsKey = 'filter_objects';

  public get uploadState(): UploadState {
    return this.documentPrintService.uploadState();
  }

  // ############### INIT ###############

  private logger: Logger;
  private destroy$ = new Subject<void>();
  // True while this page is visible; guards the pendingShare effect from loading before first entry.
  private isPageActive = false;

  constructor(
    private documentPrintService: DocumentPrintService,
    private errorService: ErrorService,
    private loggingService: LoggingService,
    private modalCtrl: ModalController,
    private networkService: NetworkService,
    private toastCtrl: ToastController,
    private ngZone: NgZone,
    private pagingService: PagingService,
    private popoverCtrl: PopoverController,
    private printerService: PrinterService,
    private router: Router,
    private sanitizationService: SanitizationService,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService,
    private pageTitleService: PageTitleService,
    private platformService: PlatformService,
    private shareExtensionService: ShareExtensionService,
    private translateService: TranslateService,
  ) {
    this.logger = this.loggingService.getLogger("[DocumentPrintPage]");
    this.logger.entry("ctor");

    // Loads a shared file that arrives while already on this page (no navigation, so ionViewDidEnter won't re-fire).
    effect(() => {
      const share = this.shareExtensionService.pendingShare();
      if (share && this.isPageActive) {
        this.logger.info('ShareExtension: page active, loading pending shared file');
        this.loadPendingSharedFile(share);
      }
    });
  }

  ngOnInit(): void {
    const methodName = "ngOnInit";
    this.logger.info(methodName);
    this.pageTitleService.setTitle('UploadForPrinting');
    this.documentPrintService.reset();
    this.tenantService.refreshStoredTenant()
      .pipe(
        tap(() => this.logger.info(methodName + ': tenant refreshed')),
        switchMap(() => this.userService.refreshCurrentUser()),
        tap(() => this.logger.info(methodName + ': user refreshed')),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.loadPrinters(),
        error: (err) => {
          this.logger.error(methodName, 'tenant/user refresh failed: ' + String(err));
          this.errorService.handleHttpClientResponseError(err, 'GET', methodName).subscribe();
        }
      });
  }

  // Resets to show the "Upload For Printing" button again, but leaves an in-flight upload untouched.
  ionViewWillEnter(): void {
    const methodName = "ionViewWillEnter";
    const state = this.uploadState;
    if (state === 'done' || state === 'error') {
      this.logger.info(methodName + ': resetting uploadState from ' + state + ' to idle');
      this.documentPrintService.reset();
      this.clearSelection();
      this.selectedQueue.set(null);
      this.validationError.set(null);
    }
  }

  // Primary path for consuming a pending shared file after navigation.
  ionViewDidEnter(): void {
    this.isPageActive = true;

    if (!this.platformService.isIOS()) { return; }

    const share = this.shareExtensionService.pendingShare();
    if (share) {
      this.logger.info('ShareExtension: ionViewDidEnter loading pending shared file');
      this.loadPendingSharedFile(share);
    }
  }

  // No share cleanup here: the App Group copy is already gone by the time startReading() resolves, and a read still in flight deletes its own file when it settles.
  ionViewWillLeave(): void {
    this.logger.info('Share Extension: ionViewWillLeave — isPageActive=false uploadState=' + this.uploadState);
    this.isPageActive = false;
  }

  ngOnDestroy(): void {
    const methodName = "ngOnDestroy";
    this.logger.info(methodName);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ############### PRINTER / QUEUE ###############

  private loadPrinters(): void {
    const methodName = "loadPrinters";
    this.logger.info(methodName);

    const tenant = this.tenantService.tenantSignal();
    if (!tenant?.links?.networks) {
      this.fetchQueues();
      return;
    }

    this.tenantService.getTenantNetworks(tenant.links.networks, [], 0, 5, 'NAME', 'ASC', '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (networksList) => {
          this.tenantNetworks = networksList;
          this.storageService.getItemFromLocalStorage(this.filterObjectsKey, 'loadPrinters()')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (stored: any[]) => {
                stored = stored ?? [];
                this.storedNetworkFilters.set(stored);
                this.storedNetworkIds = stored.map(nw => nw['id']);
                this.fetchQueues();
              },
              error: () => this.fetchQueues()
            });
        },
        error: () => this.fetchQueues()
      });
  }

  private fetchQueues(): void {
    const methodName = "fetchQueues";
    this.logger.info(methodName);

    const queueParameters = this.pagingService.getQueueParameters(this.currentPage, this.pageSize, [], this.searchTerm || '', []);
    queueParameters.printerTypes = ['NETWORK', 'VIRTUAL', 'FREEDOM'];
    this.storedNetworkFilters().forEach(nw => queueParameters.networks.push(nw['id']));

    this.printerService.getPrinterQueuesList(queueParameters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any[]) => {
          const pageObj     = result.pop();
          const printersObj = result.pop();
          const queues: IQueue[] = result;
          this.printerListData = printersObj?.printers ?? [];
          this.setPrinterDetailsOnQueues(queues, this.printerListData);
          this.queueList.set(queues.filter(q => q?.name));
          this.pagingObject.set(pageObj?.page ?? {});
        },
        error: (err) => {
          this.errorService.handleHttpClientResponseError(err, 'POST', methodName);
        }
      });
  }

  private setPrinterDetailsOnQueues(queues: IQueue[], printers: IPrinter[]): void {
    const methodName = "setPrinterDetailsOnQueues";
    this.logger.info(methodName);
    queues.forEach(q => {
      printers.forEach(p => {
        if (q.signId === p.signId) {
          q.printerDetails.location = p.location as unknown as string;
          q.printerDetails.colorSupport = p.colorSupport;
          q.printerDetails.duplexSupport = p.duplexSupport;
        }
      });
    });
  }

  public onQueueSelect(queue: IQueue): void {
    const methodName = "onQueueSelect";
    this.logger.info(methodName);
    this.selectedQueue.set(queue);
  }

  public getNextPage(showPrevious = false): void {
    const methodName = "getNextPage";
    this.logger.info(methodName);
    showPrevious ? this.currentPage-- : this.currentPage++;
    this.fetchQueues();
  }

  public dismissSelectedFilter(filter: any): void {
    const methodName = "dismissSelectedFilter";
    this.logger.info(methodName);
    const updated = this.storedNetworkFilters().filter(f => f !== filter);
    this.storedNetworkFilters.set(updated);
    this.storedNetworkIds = updated.map(nw => nw['id']);
    this.storageService.addItemToLocalStorage(this.filterObjectsKey, updated)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => { this.currentPage = 0; this.fetchQueues(); } });
  }

  public async presentPopover(myEvent: Event): Promise<void> {
    const methodName = "presentPopover";
    this.logger.info(methodName);

    const dataPackage = {
      type: 'filterPrinterList',
      networks: this.tenantNetworks,
      resourceId: null,
      storedNetworks: this.storedNetworkFilters()
    };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      event: myEvent,
      translucent: true,
      componentProps: { dataPackage },
      cssClass: 'filterPrinterList',
      animated: true
    });

    await popover.present();

    await popover.onDidDismiss().then((result) => {
      if (result.data === 'clearAllFilters') {
        this.storageService.removeStoredItems([this.filterObjectsKey])
          .pipe(takeUntil(this.destroy$))
          .subscribe({ next: () => { this.currentPage = 0; this.storedNetworkFilters.set([]); this.storedNetworkIds = []; this.fetchQueues(); } });
      } else if (result.data === 'showNetworksModal') {
        this.presentNetworksListModal();
      }
    });
  }

  private async presentNetworksListModal(): Promise<void> {
    const methodName = "presentNetworksListModal";
    this.logger.info(methodName);

    const dataPackage = {
      networks: this.tenantNetworks,
      resourceId: null,
      storedNetworks: this.storedNetworkFilters(),
      cssClass: 'networksList'
    };

    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      componentProps: { dataPackage },
      cssClass: 'networksList'
    });

    await modal.present();

    await modal.onDidDismiss().then(() => {
      this.storageService.getItemFromLocalStorage(this.filterObjectsKey, methodName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (stored: any[]) => {
            stored = stored ?? [];
            this.storedNetworkFilters.set(stored);
            this.storedNetworkIds = stored.map(nw => nw['id']);
            this.currentPage = 0;
            this.fetchQueues();
          }
        });
    });
  }

  public onSearch(event: CustomEvent): void {
    const methodName = "onSearch";
    this.logger.info(methodName);
    this.searchTerm = this.sanitizationService.sanitizeTextInput(event.detail.value ?? '');
    this.currentPage = 0;
    this.fetchQueues();
  }

  // ############### SHARE EXTENSION ###############

  // Reads the shared file and feeds it into the normal file-selection path; always resets upload state first.
  private loadPendingSharedFile(share: IShareItem): void {
    this.documentPrintService.reset();
    this.clearSelection();
    this.logger.info('ShareExtension: loadPendingSharedFile ' + JSON.stringify({ displayName: share.displayName }));

    this.shareExtensionService.startReading(share.id)
      .then(file => {
        this.logger.info('ShareExtension: shared file loaded ' + JSON.stringify({ name: file.name, size: file.size }));
        this.ngZone.run(() => {
          void this.setFile(file);
        });
      })
      .catch(err => {
        const msg = String(err);
        if (err instanceof ShareSupersededError) {
          // Normal outcome: a newer share arrived before this read began, and that newer share is already being loaded — no error to show.
          this.logger.info('loadPendingSharedFile ShareExtension: superseded — ' + JSON.stringify({ error: msg }));
          return;
        }
        this.logger.error('loadPendingSharedFile ShareExtension: FAILED — ' + JSON.stringify({ error: msg }));
        // get() emits once the active language's captions are in, so the toast is never raised untranslated.
        this.translateService.get('CouldNotLoadSharedFile')
          .pipe(takeUntil(this.destroy$))
          .subscribe((message: string) => {
            this.toastCtrl.create({
              message,
              color: 'danger',
              duration: 4000,
              position: 'top'
            }).then(t => t.present());
          });
      });
  }

  // ############### FILE PICKER ###############

  public onFileInputChange(event: Event): void {
    const methodName = "onFileInputChange";
    this.logger.info(methodName);

    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const rawFile = input.files[0];

    const error = this.documentPrintService.validateFile(rawFile);
    if (error) {
      this.validationError.set(error);
      this.clearSelection();
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target!.result as ArrayBuffer;
        const blob = new Blob([arrayBuffer], { type: rawFile.type || 'application/pdf' });
        const file = Object.assign(blob, {
          name: rawFile.name,
          lastModified: rawFile.lastModified
        }) as unknown as File;

        this.ngZone.run(() => {
          void this.setFile(file);
        });
      } catch (err) {
        this.logger.error(methodName + ' Failed to read file — ' + JSON.stringify({ error: err instanceof Error ? err.message : String(err) }));
        this.ngZone.run(() => this.validationError.set('FileReadError'));
      }
    };

    reader.onerror = () => {
      this.logger.error(methodName, 'FileReader error');
      this.ngZone.run(() => this.validationError.set('FileReadError'));
    };

    reader.readAsArrayBuffer(rawFile);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  public onDragLeave(): void {
    this.isDragOver.set(false);
  }

  public async onDrop(event: DragEvent): Promise<void> {
    const methodName = "onDrop";
    this.logger.info(methodName);

    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      await this.setFile(file);
    }
  }

  // Same path for a shared file and a user-picked one: neither owns anything on disk by the time it gets here.
  private async setFile(file: File): Promise<void> {
    const methodName = "setFile";
    const requestId = ++this.setFileRequestId;
    // Drop the previous file up front so it cannot be uploaded while the new one is still being checked.
    this.selectedFile.set(null);
    this.validationError.set(null);

    const error = this.documentPrintService.validateFile(file)
      ?? await this.documentPrintService.validateFileContent(file);
    // A newer file was picked while the content check was running; its result wins.
    if (requestId !== this.setFileRequestId) return;

    this.ngZone.run(() => {
      this.validationError.set(error);
      if (!error) {
        this.selectedFile.set(file);
        this.logger.info(methodName + ' ShareExtension: file selected ' + JSON.stringify({ name: file.name, size: file.size }));
      } else {
        this.clearSelection();
        this.logger.warn(methodName + ' ShareExtension: validation FAILED — selectedFile set to null. error=' + error);
      }
    });
  }

  private clearSelection(): void {
    this.setFileRequestId++;
    this.selectedFile.set(null);
  }

  // ############### UPLOAD ###############

  public get canUpload(): boolean {
    return (
      this.selectedFile() !== null &&
      this.selectedQueue() !== null &&
      this.uploadState === 'idle'
    );
  }

  public onUpload(): void {
    const methodName = "onUpload";
    const file  = this.selectedFile();
    const queue = this.selectedQueue();
    const user  = this.userService.userSignal();
    this.logger.info(
      methodName + ' canUpload=' + this.canUpload + ' uploadState=' + this.uploadState + ' user=' + (user ? 'ok' : 'null') + ' '
      + JSON.stringify({ file: file ? { name: file.name, size: file.size } : null, queue: queue ? queue.name : null })
    );

    if (!file || !queue || !user) {
      this.logger.warn(methodName, 'onUpload: guard blocked — file=' + !!file + ' queue=' + !!queue + ' user=' + !!user);
      return;
    }

    const printJobsUrl = this.sanitizationService.validateApiUrl(user.links.printJobs);
    const printerUrl   = this.sanitizationService.validateApiUrl(queue.links.printer);
    const queueUrl     = this.sanitizationService.validateApiUrl(queue.links.self);

    if (!printJobsUrl || !printerUrl || !queueUrl) {
      this.logger.error(methodName, 'Invalid HATEOAS URL(s) — aborting upload');
      return;
    }

    this.documentPrintService.uploadDocument(
      printJobsUrl,
      printerUrl,
      queueUrl,
      file
    ).pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.documentPrintService.uploadState.set('done');
        void this.router.navigate(['/print-jobs']);
      },
      error: () => {
        // already handled by DocumentPrintService.uploadDocument catchError
      }
    });
  }
}
